import { useLocalization, Localized } from "@fluent/react";
import { Button, Dialog, TextInput } from "@narmi/design_system";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { DialogProps } from "@narmi/design_system/dist/types/Dialog";
import { useAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { modules } from "byzantine";

import styles from "./LoanAutopayDialog.module.scss";
import NewThreadDialog from "../NewThreadDialog";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import ComboAccountSelector from "../ComboAccountSelector";

const { useLoanAutopayManageForm } = modules.loanPayment;

interface LoanAutopayDialogProps
  extends Pick<DialogProps, "isOpen" | "onUserDismiss"> {
  loanAccountId: API.AccountId;
  loanAccountNumber: string;
  paymentDate?: string;
  paymentAmount?: string | null;
  currentSourceAccountId?: string;
}

const LoanAutopayDialog = ({
  isOpen,
  onUserDismiss,
  loanAccountId,
  loanAccountNumber,
  paymentDate,
  paymentAmount,
  currentSourceAccountId = "",
}: LoanAutopayDialogProps) => {
  const accounts = useAccounts();
  const { l10n } = useLocalization();
  const { loan_payment_by_external_ach: allowPaymentByExternalAch = false } =
    useUserFeatures();
  const {
    sourceAccountName,
    sourceAccountNumberLastFour,
    showError,
    accountChangeHandler,
    submitHandler,
    selectedSourceAccountId: _sourceAccountId,
  } = useLoanAutopayManageForm(currentSourceAccountId as API.AccountId);
  const selectedSourceAccountId = _sourceAccountId || ("" as string);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);

  const goToMessages = useCallback(() => {
    submitHandler(() => {
      onUserDismiss?.();
      setIsMessageDialogOpen(true);
    });
  }, [onUserDismiss, submitHandler]);

  // Exclude current loan account and external accounts if not enabled by FI
  const eligibleAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        if (
          a.id === loanAccountId ||
          (a.isExternal() && !allowPaymentByExternalAch)
        ) {
          return false;
        }
        return a.isValidTransferSource();
      }),
    [accounts, loanAccountId, allowPaymentByExternalAch]
  );

  const footerButtons = useMemo(
    () => (
      <div className={styles["button-container"]}>
        <Button
          label={l10n.getString("label-cancel")}
          kind="negative"
          onClick={onUserDismiss}
        />
        <Button
          label={l10n.getString("loan-autopay-dialog-button-success")}
          onClick={goToMessages}
        />
      </div>
    ),
    [l10n, onUserDismiss, goToMessages]
  );

  useEffect(() => {
    if (!isOpen) {
      accountChangeHandler(currentSourceAccountId);
    }
  }, [isOpen, currentSourceAccountId, accountChangeHandler]);

  return (
    <>
      <Dialog
        width="552px"
        isOpen={isOpen}
        onUserDismiss={onUserDismiss}
        title={l10n.getString("loan-autopay-dialog-title")}
        footer={footerButtons}
      >
        <div className={styles["dialog-container"]}>
          <div>
            <Localized id="loan-autopay-dialog-instructions" />
          </div>
          <ComboAccountSelector
            label={l10n.getString("label-account")}
            accounts={eligibleAccounts}
            value={selectedSourceAccountId}
            onChange={accountChangeHandler}
            isDestination={false}
            showAddExternalAccountLink={false}
            errorText={showError ? l10n.getString("error-required") : undefined}
          />
          {paymentDate && paymentAmount && (
            <>
              <TextInput
                disabled
                value={paymentDate}
                label={l10n.getString("label-date")}
                inputMode="numeric"
              />
              <TextInput
                disabled
                value={paymentAmount}
                label={l10n.getString("label-amount")}
                inputMode="numeric"
              />
            </>
          )}
          <div className={styles["dialog-disclaimer-text"]}>
            <Localized id="loan-autopay-dialog-disclaimer" />
          </div>
        </div>
      </Dialog>
      {isMessageDialogOpen && (
        <NewThreadDialog
          hideTeamSelector
          replyTime={""}
          isDialogOpen
          closeDialog={() => setIsMessageDialogOpen(false)}
          defaultSubject={l10n.getString(
            "loan-autopay-secure-message-subject-v2",
            {
              isUpdate: `${!!currentSourceAccountId}`,
              loanAccountNumber,
            }
          )}
          defaultBody={l10n.getString("loan-autopay-secure-message-body-v3", {
            isUpdate: `${!!currentSourceAccountId}`,
            loanAccountNumber,
            sourceAccountName,
            sourceAccountNumberLastFour,
          })}
        />
      )}
    </>
  );
};

export default memo(LoanAutopayDialog);
